import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Leaves.css';

export default function Leaves({ employee }) {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [leaveType, setLeaveType] = useState('casual');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balData, histData] = await Promise.all([
        api.getLeaveBalance(),
        api.getMyLeaves(new Date().getFullYear())
      ]);
      setBalance(balData.balance);
      setHistory(histData.leaves || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!fromDate || !reason) {
      setError('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const res = await api.applyLeave({
        leave_type: leaveType,
        from_date: fromDate,
        to_date: toDate || fromDate,
        reason
      });
      if (res.error) {
        setError(res.error);
        return;
      }
      setMessage('Leave request submitted successfully! HR will review within 24 hours.');
      setFromDate(''); setToDate(''); setReason('');
      loadData();
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const BADGE = {
    approved: { label: 'Approved', cls: 'bpresent' },
    rejected: { label: 'Rejected', cls: 'babsent' },
    pending: { label: 'Pending', cls: 'bpending' },
  };

  const balanceList = balance ? [
    { type: 'Casual', remaining: balance.casual?.remaining || 0, total: balance.casual?.total || 3, color: 'red' },
    { type: 'Sick', remaining: balance.sick?.remaining || 0, total: balance.sick?.total || 5, color: 'green' },
    { type: 'Annual', remaining: balance.annual?.remaining || 0, total: balance.annual?.total || 12, color: 'blue' },
  ] : [];

  return (
    <div className="leaves-page">
      <div className="leaves-section-label">Leave Balance</div>

      {loading ? (
        <div style={{textAlign:'center', padding:'20px', color:'var(--muted)'}}>Loading...</div>
      ) : (
        <div className="leaves-balance">
          {balanceList.map((b, i) => (
            <div key={i} className="balance-card">
              <div className="balance-type">{b.type}</div>
              <div className={`balance-num ${b.color}`}>{b.remaining}</div>
              <div className="balance-total">of {b.total} days</div>
              <div className="balance-bar">
                <div className="balance-fill" style={{
                  width: `${Math.min((b.remaining / b.total) * 100, 100)}%`,
                  background: b.color === 'green' ? 'var(--green)' : b.color === 'blue' ? 'var(--blue)' : 'var(--red)'
                }}/>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="leaves-section-label">Apply for Leave</div>
      <div className="leave-form-card">
        {message && <div style={{background:'#dcfce7', border:'1px solid #86efac', borderRadius:'9px', padding:'10px 14px', fontSize:'12.5px', color:'#15803d', marginBottom:'12px'}}>{message}</div>}
        {error && <div style={{background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:'9px', padding:'10px 14px', fontSize:'12.5px', color:'#b91c1c', marginBottom:'12px'}}>{error}</div>}

        <div className="form-group">
          <label className="form-label">Leave Type</label>
          <select className="form-control" value={leaveType} onChange={e => setLeaveType(e.target.value)}>
            <option value="casual">Casual Leave</option>
            <option value="sick">Sick Leave</option>
            <option value="annual">Annual Leave</option>
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">From Date *</label>
            <input type="date" className="form-control" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">To Date</label>
            <input type="date" className="form-control" value={toDate} onChange={e => setToDate(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Reason *</label>
          <textarea className="form-control" rows="3" placeholder="Briefly describe your reason..." value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <button className="submit-btn" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Request'}
        </button>
      </div>

      <div className="leaves-section-label" style={{marginTop: 16}}>Leave History</div>
      <div className="leave-history-card">
        {history.length === 0 ? (
          <div style={{padding:'20px', textAlign:'center', color:'var(--muted)', fontSize:'13px'}}>No leave requests yet</div>
        ) : history.map((h, i) => (
          <div key={i} className="history-item">
            <div className="history-left">
              <div className="history-type">{h.leave_type?.charAt(0).toUpperCase() + h.leave_type?.slice(1)} Leave</div>
              <div className="history-dates">
                {new Date(h.from_date).toLocaleDateString('en-PK', {day:'numeric', month:'short'})}
                {h.from_date !== h.to_date ? ` – ${new Date(h.to_date).toLocaleDateString('en-PK', {day:'numeric', month:'short'})}` : ''}
                · {h.total_days} day{h.total_days > 1 ? 's' : ''}
              </div>
              <div className="history-reason">{h.reason}</div>
            </div>
            <span className={`badge ${BADGE[h.status]?.cls || 'bpending'}`}>
              {BADGE[h.status]?.label || 'Pending'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}